import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { pushAnalyticsEvent } from 'helpers/analytics';
import { Typography, Divider, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useStore } from 'providers';
import { sendRedemptionCode } from 'services/loyaltyGateway';
import { useFetchFromService, usePageTitle } from 'hooks';
import { ROUTES } from 'constants/routes';
import getUserDevice from 'utils/getUserDevice';
import axios from 'axios';
import Layout from 'layouts';
import Loader from 'components/Loader';
import Alert, { AlertState } from 'components/Alert';
import ButtonWithSpinner from 'components/ButtonWithSpinner';
import QrScannerModal from 'views/modals/QrScanner';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ClaimZsImage from 'assets/images/img-claim-zs.jpg';
import { AZQR_URL } from 'constants/default';

import {
  StyledContainer,
  StyledPaper,
  Title,
  Hero,
  StyledBalanceCard,
  Image,
  StyledBox,
  StyledTextField,
  Footer,
  StyledLogo,
  Amount,
  StyledInfoRounded,
} from './ClaimZs.styles';

const pageTitle = 'Claim Zs';
const redemptionAmount = 10;
const initialState: AlertState = { type: 'success', isVisible: false, text: '' };

const ClaimZs = () => {
  usePageTitle(pageTitle);

  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const receiptCode = query.get('receiptCode');

  const isMobileDevice = getUserDevice();
  const showQrScanner = !!isMobileDevice;

  const { store } = useStore();
  const { userData } = store;

  const fetchUserData = useFetchFromService('userData');

  const [redemptionCode, setRedemptionCode] = useState('');
  const [alert, setAlert] = useState<AlertState>(initialState);
  const [isSending, setIsSending] = useState(false);
  const [isQrScannerVisible, setIsQrScannerVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasBeenScanned, setHasBeenScanned] = useState(false);

  const hasError = alert.type === 'error';
  const isDisabled = !redemptionCode || isSending || hasError;

  useEffect(() => {
    if (receiptCode) {
      setRedemptionCode(receiptCode.toUpperCase());
    }
  }, [receiptCode]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setAlert((prev) => ({ ...prev, type: 'success', isVisible: false }));
    setRedemptionCode(value);
    setHasBeenScanned(false);
  };

  const handleOnRedeem = async () => {
    setIsSending(true);
    try {
      const response = await sendRedemptionCode(redemptionCode);
      await fetchUserData();
      setAlert({
        type: 'success',
        isVisible: true,
        text: `${response.zsEarned} Zs have been added to your balance!`,
      });
      setIsSending(false);
      setRedemptionCode('');
      const description = hasBeenScanned ? 'Scanned by QR' : 'Entered manually';
      pushAnalyticsEvent({ event: 'userRedeemedCode', description });
    } catch (e) {
      let errorMessage = 'Uh oh! This code seems to be invalid';
      if (axios.isAxiosError(e) && e.response?.status === 400 && e.response?.data.message) {
        errorMessage = e.response?.data.message;
      }
      setAlert({ type: 'error', isVisible: true, text: errorMessage });
      setIsSending(false);
    }
  };

  const handleOnQrScannerToggle = useCallback(() => {
    setIsQrScannerVisible((isVisible) => !isVisible);
  }, []);

  const handleTooltipToggle = () => {
    setShowTooltip(!showTooltip);
    if (!showTooltip) {
      pushAnalyticsEvent({ event: 'restaurantVisitToolTipViewed' });
    }
  };

  const handleOnCapture = useCallback(
    (code: string) => {
      const isCorrectURL = code.startsWith(AZQR_URL);

      if (isCorrectURL) {
        const QRText = code.replace(AZQR_URL, '').toUpperCase();
        setRedemptionCode(QRText);
        setAlert(initialState);
      } else {
        setRedemptionCode('');
        setAlert({ type: 'error', isVisible: true, text: 'QR Code not recognised' });
      }
      handleOnQrScannerToggle();
      setHasBeenScanned(true);
    },
    [handleOnQrScannerToggle]
  );

  const InputWithCamera = () => (
    <InputAdornment position="end">
      <IconButton onClick={handleOnQrScannerToggle} aria-label="Camera Button">
        <PhotoCameraIcon />
      </IconButton>
    </InputAdornment>
  );

  const inputProps = (showQrScanner && { endAdornment: <InputWithCamera /> }) || undefined;
  const inputLabel = showQrScanner ? 'Enter or scan your receipt code' : 'Enter your receipt code';

  const tooltipList = [
    '1st visit = earn 10 Zs',
    '2nd visit = earn 20 Zs',
    '3rd visit = earn 30 Zs',
    '4th visit = earn 40 Zs + get a surprise Perk',
  ];

  const tooltipText = tooltipList.map((text, idx) => (
    <Typography key={`tooltip-${idx + 1}`} gutterBottom>
      {text}
    </Typography>
  ));

  return (
    <Layout ariaLabel="Claim Zs page">
      {userData ? (
        <StyledContainer>
          <StyledPaper>
            <Title variant="h1">{pageTitle}</Title>

            <Hero>
              <StyledBalanceCard points={userData.pointsDetails.points} pageTitle={pageTitle} />
              <Image src={ClaimZsImage} />
            </Hero>

            <StyledBox>
              <Typography variant="h3" gutterBottom>
                Earn Zs for your takeaway order or restaurant dining experience
              </Typography>
              <Typography variant="body1">{inputLabel}</Typography>

              <StyledTextField
                variant="filled"
                aria-label="Redemption Code"
                value={redemptionCode}
                fullWidth
                type="text"
                onChange={handleOnChange}
                placeholder="Receipt Code"
                error={hasError}
                autoComplete="off"
                InputProps={inputProps}
              />

              {alert.isVisible && <Alert text={alert.text} type={alert.type} />}
            </StyledBox>
            <StyledBox>
              <Typography variant="body1" gutterBottom>
                This unique code can be found on your order receipt and contains letters and
                numbers. It can only be used once.
              </Typography>

              <Typography variant="body1" gutterBottom>
                Read more about <a href={ROUTES.HOW_IT_WORKS}>How it Works</a>.
              </Typography>
            </StyledBox>
            <Divider />

            <StyledBox>
              <Typography variant="h5" gutterBottom>
                Delivery:
                <Amount> +{redemptionAmount}</Amount>
                <StyledLogo />
              </Typography>

              <Typography variant="h5" gutterBottom>
                Click &amp; Collect:
                <Amount> +{redemptionAmount}</Amount>
                <StyledLogo />
              </Typography>

              <Typography variant="h5" gutterBottom>
                Restaurant visit
                <Tooltip
                  title={tooltipText}
                  arrow
                  open={showTooltip}
                  onOpen={handleTooltipToggle}
                  onClose={handleTooltipToggle}
                >
                  <StyledInfoRounded onClick={handleTooltipToggle} />
                </Tooltip>
              </Typography>
            </StyledBox>

            <Footer>
              <ButtonWithSpinner
                onClick={handleOnRedeem}
                color="primary"
                variant="contained"
                fullWidth
                showSpinner={isSending}
                disabled={isDisabled}
              >
                Apply Code
              </ButtonWithSpinner>
            </Footer>
          </StyledPaper>

          <QrScannerModal
            isOpen={isQrScannerVisible}
            onClose={handleOnQrScannerToggle}
            onCapture={handleOnCapture}
          />
        </StyledContainer>
      ) : (
        <Loader />
      )}
    </Layout>
  );
};

export default ClaimZs;
